/**
 * Fiche d'un arrêté — « Relecture et validation » (recette du 28/09).
 * Le circuit (défaut + modèle) est recopié à la création ; le rédacteur l'AJUSTE ici avant
 * l'envoi, suit les réponses (signées ou non, sur une version antérieure), saisit l'avis
 * reçu par e-mail d'un externe, et voit l'avant/après des corrections faites par un valideur.
 */
import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Mail, Send, Trash2, History } from 'lucide-react';
import { Alert, Button, Card } from '@/components/ui';
import { api, type ActeIntervenantVue } from '@/lib/api';

type Correction = Awaited<ReturnType<typeof api.relecture.corrections>>[number];
type Pers = Awaited<ReturnType<typeof api.workflows.annuaireDestinataires>>['users'][number];
type Rel = Awaited<ReturnType<typeof api.serviceTechnique.arretes.relecteurs.list>>['items'][number];
const DECISION: Record<string, string> = { favorable: 'Validé / favorable', reserves: 'Avec réserves', defavorable: 'Défavorable / renvoyé' };
const ORIGINE: Record<string, string> = { vpc: 'dans VPC', lien: 'par son lien', email: 'reçu par e-mail' };
const affiche = (v: unknown) => (Array.isArray(v) ? v.map((x) => (typeof x === 'string' ? x : `Art. ${(x as { numero: number }).numero} : ${(x as { texte: string }).texte}`)).join(' · ') : String(v ?? '—'));

export function RelectureValidationCard({ acteId, statut, onEnvoyerSansCircuit, onChange }: {
  acteId: string; statut: string; onEnvoyerSansCircuit: () => void; onChange: () => void;
}) {
  const [liste, setListe] = useState<ActeIntervenantVue[]>([]);
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [personnel, setPersonnel] = useState<Pers[]>([]);
  const [externes, setExternes] = useState<Rel[]>([]);
  const [ajoutV, setAjoutV] = useState(''); const [ajoutR, setAjoutR] = useState('');
  const [saisie, setSaisie] = useState<{ id: string; avis: 'favorable' | 'reserves' | 'defavorable'; commentaire: string } | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);
  const [occupe, setOccupe] = useState(false);
  const modifiable = statut === 'genere';

  const charger = useCallback(async () => {
    const [l, c] = await Promise.all([api.relecture.intervenants(acteId), api.relecture.corrections(acteId).catch(() => [])]);
    setListe(l); setCorrections(c);
  }, [acteId]);
  useEffect(() => {
    void charger().catch(() => undefined);
    api.workflows.annuaireDestinataires().then((a) => setPersonnel(a.users.filter((u) => ['agent', 'manager', 'admin'].includes(u.role)))).catch(() => undefined);
    api.serviceTechnique.arretes.relecteurs.list().then((r) => setExternes(r.items)).catch(() => undefined);
  }, [charger]);

  const agir = async (f: () => Promise<unknown>, t?: string) => {
    setOccupe(true); setMsg(null);
    try { await f(); await charger(); if (t) setMsg({ ok: true, t }); onChange(); }
    catch (e) { setMsg({ ok: false, t: e instanceof Error ? e.message : 'Action impossible' }); }
    finally { setOccupe(false); }
  };
  const valideurs = liste.filter((x) => x.role === 'valideur');
  const relecteurs = liste.filter((x) => x.role === 'relecteur');

  const ligne = (x: ActeIntervenantVue) => (
    <li key={x.id} className="rounded-r-md border border-border bg-bg/40 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-ink">{x.nom}{x.email && !x.userId ? <span className="ml-2 text-xs font-normal text-ink-muted">{x.email}</span> : null}</span>
        <span className="flex items-center gap-2 text-xs text-ink-muted">
          {x.responduAt
            ? <>{DECISION[x.avis ?? ''] ?? x.avis} · {ORIGINE[x.avisOrigine ?? ''] ?? ''} · {new Date(x.responduAt).toLocaleDateString('fr-FR')}</>
            : x.solliciteAt ? 'En attente de réponse' : 'Pas encore sollicité'}
          {modifiable && !x.responduAt && (
            <Button type="button" variant="ghost" size="sm" disabled={occupe} aria-label={`Retirer ${x.nom}`}
              onClick={() => void agir(() => api.relecture.retirer(x.id))}><Trash2 className="h-4 w-4 text-danger" aria-hidden="true" /></Button>
          )}
        </span>
      </div>
      {x.avisSigne && <p className="mt-1 text-xs text-success">Avis signé (Signature VPC) · empreinte {x.empreinte}</p>}
      {x.surVersionAnterieure && <p className="mt-1 text-xs text-warning">Avis rendu sur une version antérieure, corrigée ensuite par un valideur.</p>}
      {x.commentaire && <p className="mt-1 text-xs text-ink">« {x.commentaire} »</p>}
      {!x.userId && x.solliciteAt && !x.responduAt && statut === 'relecture_juridique' && (
        saisie?.id === x.id ? (
          <div className="mt-2 space-y-2">
            <label className="block text-xs text-ink">Avis reçu par e-mail
              <select className="ml-2 h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm" value={saisie.avis}
                onChange={(e) => setSaisie({ ...saisie, avis: e.target.value as 'favorable' | 'reserves' | 'defavorable' })}>
                <option value="favorable">Favorable</option><option value="reserves">Avec réserves</option><option value="defavorable">Défavorable</option>
              </select></label>
            <label className="block text-xs text-ink">Ce qu’il a répondu
              <textarea rows={2} className="mt-1 w-full rounded-r-md border border-border bg-surface/60 px-2 py-1 text-sm" value={saisie.commentaire}
                onChange={(e) => setSaisie({ ...saisie, commentaire: e.target.value })} /></label>
            <div className="flex gap-2">
              <Button type="button" size="sm" disabled={occupe} onClick={() => void agir(() => api.relecture.avisRecu(x.id, saisie.avis, saisie.commentaire.trim() || null), 'Avis enregistré.').then(() => setSaisie(null))}>Enregistrer l’avis</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setSaisie(null)}>Annuler</Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="ghost" size="sm" className="mt-1" onClick={() => setSaisie({ id: x.id, avis: 'favorable', commentaire: '' })}>Saisir l’avis reçu par e-mail</Button>
        )
      )}
    </li>
  );

  return (
    <Card className="mt-4 p-4">
      <h2 className="text-sm font-semibold text-ink">Relecture et validation</h2>
      <p className="mt-1 text-xs text-ink-muted">Les valideurs (comptes de l’application) décident et peuvent corriger ; les relecteurs externes donnent un avis signé. L’arrêté part seul à la signature quand tous ont répondu.</p>
      {msg && <div className="mt-3"><Alert variant={msg.ok ? 'success' : 'error'}>{msg.t}</Alert></div>}

      <h3 className="mt-4 flex items-center gap-2 text-sm font-medium text-ink"><ShieldCheck className="h-4 w-4 text-tech-fg" aria-hidden="true" /> Valideurs</h3>
      {valideurs.length ? <ul className="mt-2 space-y-1.5">{valideurs.map(ligne)}</ul> : <p className="mt-1 text-xs text-ink-muted">Aucun valideur : la validation reste manuelle.</p>}
      <h3 className="mt-4 flex items-center gap-2 text-sm font-medium text-ink"><Mail className="h-4 w-4 text-tech-fg" aria-hidden="true" /> Relecteurs externes</h3>
      {relecteurs.length ? <ul className="mt-2 space-y-1.5">{relecteurs.map(ligne)}</ul> : <p className="mt-1 text-xs text-ink-muted">Aucun relecteur externe.</p>}

      {modifiable && (
        <div className="mt-4 space-y-2 border-t border-border pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={`aj-v-${acteId}`} className="sr-only">Ajouter un valideur</label>
            <select id={`aj-v-${acteId}`} value={ajoutV} onChange={(e) => setAjoutV(e.target.value)} className="h-9 min-w-[14rem] rounded-r-md border border-border bg-surface/60 px-2 text-sm">
              <option value="">Ajouter un valideur…</option>
              {personnel.filter((u) => !valideurs.some((v) => v.userId === u.id)).map((u) => <option key={u.id} value={u.id}>{u.label}{u.service ? ` — ${u.service}` : ''}</option>)}
            </select>
            <Button type="button" size="sm" variant="secondary" disabled={!ajoutV || occupe} onClick={() => void agir(() => api.relecture.designer(acteId, { userIds: [ajoutV] })).then(() => setAjoutV(''))}>Ajouter</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={`aj-r-${acteId}`} className="sr-only">Ajouter un relecteur externe</label>
            <select id={`aj-r-${acteId}`} value={ajoutR} onChange={(e) => setAjoutR(e.target.value)} className="h-9 min-w-[14rem] rounded-r-md border border-border bg-surface/60 px-2 text-sm">
              <option value="">Ajouter un relecteur externe…</option>
              {externes.filter((r) => r.actif !== false && !relecteurs.some((x) => x.email === r.email)).map((r) => <option key={r.id} value={r.id}>{r.nom} — {r.email}</option>)}
            </select>
            <Button type="button" size="sm" variant="secondary" disabled={!ajoutR || occupe} onClick={() => void agir(() => api.relecture.designer(acteId, { relecteurIds: [ajoutR] })).then(() => setAjoutR(''))}>Ajouter</Button>
          </div>
          <Button type="button" className="w-full" disabled={occupe}
            onClick={() => (liste.length ? void agir(() => api.relecture.envoyer(acteId), 'Envoyé : les valideurs sont prévenus, les relecteurs externes ont reçu le PDF et leur lien.') : onEnvoyerSansCircuit())}>
            <Send className="h-4 w-4" aria-hidden="true" /> {liste.length ? 'Envoyer en relecture et validation' : 'Envoyer en relecture (sans circuit)'}
          </Button>
        </div>
      )}

      {corrections.length > 0 && (
        <div className="mt-4 border-t border-border pt-3">
          <h3 className="flex items-center gap-2 text-sm font-medium text-ink"><History className="h-4 w-4 text-tech-fg" aria-hidden="true" /> Corrections faites par un valideur</h3>
          {corrections.map((c) => (
            <div key={c.id} className="mt-2 rounded-r-md border border-border bg-bg/40 p-3 text-xs">
              <p className="text-ink"><b>{c.auteur}</b>, le {new Date(c.le).toLocaleString('fr-FR')} — « {c.commentaire} »</p>
              <ul className="mt-1 space-y-1">
                {c.champs.map((d) => (
                  <li key={d.champ}><b className="text-ink">{d.libelle}</b> : <span className="text-ink-muted line-through">{affiche(d.avant)}</span> → <span className="text-ink">{affiche(d.apres)}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
